import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { crmSuppressionSourcesApi } from '@/api/crmSuppressionSources'
import type {
  CrmSuppressionSourceViewDto,
  CrmSuppressionSourceCreatePayload,
} from '@/types/crmSuppressionSources'

/** Result of a mutation. `handled` is true when a server 422 was already toasted by the global handler. */
export interface MutationResult {
  ok: boolean
  error?: string
  handled?: boolean
}

/**
 * Loads and maintains a company's CRM suppression sources, plus add / remove / run-now.
 *
 * Adding and running both queue background work, so the list is reloaded after each. The reconcile
 * itself finishes well after the response — the row's last-synced fields are how its outcome arrives,
 * which is why `reload` is exposed for the panel's refresh control rather than polling on a timer.
 */
export function useCrmSuppressionSources(clientCompanyId: string) {
  const [sources, setSources] = useState<CrmSuppressionSourceViewDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  const load = useCallback(async () => {
    if (!clientCompanyId) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const res = await crmSuppressionSourcesApi.list(clientCompanyId)
    // A newer load has started (company switched, or a mutation reloaded); drop this result.
    if (requestIdRef.current !== reqId) return
    if (res.error) setError(res.error)
    if (res.data) setSources(res.data)
    setLoading(false)
  }, [clientCompanyId])

  useEffect(() => {
    void load()
    return () => { requestIdRef.current++ }
  }, [load])

  const add = useCallback(async (payload: CrmSuppressionSourceCreatePayload): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await crmSuppressionSourcesApi.add(clientCompanyId, payload)
    if (res.error) return { ok: false, error: res.error, handled: res.status === 422 }
    await load()
    return { ok: true }
  }, [clientCompanyId, load])

  const remove = useCallback(async (sourceId: string): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await crmSuppressionSourcesApi.remove(clientCompanyId, sourceId)
    if (res.error) return { ok: false, error: res.error, handled: res.status === 422 }
    await load()
    return { ok: true }
  }, [clientCompanyId, load])

  const sync = useCallback(async (sourceId: string): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await crmSuppressionSourcesApi.sync(clientCompanyId, sourceId)
    if (res.error) return { ok: false, error: res.error, handled: res.status === 422 }
    // Reloads to pick up the queued state; the run's own result lands on a later reload, because the
    // worker stamps the row after the response has already gone back.
    await load()
    return { ok: true }
  }, [clientCompanyId, load])

  return { sources, loading, error, reload: load, add, remove, sync }
}
